import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FilterOptions,
  Loan,
  OffersOfApplication,
} from '../../../type/result_type';
import { ResultpageService } from '../../../service/resultpage.service';
import { ResultpagesharedService } from '../../../service/resultpageshared.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() eternalPage!: boolean;
  @Input() resultPage!: boolean;
  @Input() filterDataOptions!: FilterOptions;
  @Input() offersData!: OffersOfApplication[];
  @Input() loanFilterData!: Loan;
  @Input() twoApplicants!: boolean;

  @Input() set loading(value: boolean) {
    if (value) {
      this.search = this.searchAnimation = true;
      this.finishedSearchAnimation = this.loadedData = this.anyOffers = false;
      this.hasInstallmentBoundsFromOffers = false;
      this.loanMatchesPublishedFilters = null;
      this.showAlternativeSuffix = false;
      clearTimeout(this.setTimeoutLoadedData);
      clearTimeout(this.setTimeoutAnyOffers);
      clearTimeout(this.setTimeoutFinishedSearchAnimation);
      clearTimeout(this.setTimeoutFind);
    }

    if (window.location.hash == '#/' && value === false) {
      this.checkLoader();
    }
  }

  public minRate = 0;
  public maxRate = 0;
  public showSingleOfferInstallmentCopy = false;
  public hasInstallmentBoundsFromOffers = false;
  public loanMatchesPublishedFilters: boolean | null = null;
  public showAlternativeSuffix = false;
  public search: boolean = true;
  public searchAnimation: boolean = true;
  public loadedData: boolean = false;
  public finishedSearchAnimation: boolean = false;
  public anyOffers: boolean = false;
  private setTimeoutLoadedData;
  private setTimeoutAnyOffers;
  private setTimeoutFinishedSearchAnimation;
  private setTimeoutFind;
  public loanFilterDataValue: number;
  public loanFilterDataDuration: number;

  private filterOptionsSubscription!: Subscription;
  private loanSubscription!: Subscription;
  private offersSubscription!: Subscription;

  constructor(
    private resultpageService: ResultpageService,
    private sharedResultService: ResultpagesharedService,
  ) {}

  ngOnInit(): void {
    this.getLoanData();
    this.getFilterOptions();
    this.getOffersForInstallmentBounds();
  }

  ngOnDestroy(): void {
    this.filterOptionsSubscription?.unsubscribe();
    this.loanSubscription?.unsubscribe();
    this.offersSubscription?.unsubscribe();
  }

  get showAlternativesHeaderLine(): boolean {
    return (
      this.loanMatchesPublishedFilters === false ||
      (this.loanMatchesPublishedFilters === true &&
        !this.hasInstallmentBoundsFromOffers)
    );
  }

  get showInstallmentSummaryHeader(): boolean {
    return (
      this.loanMatchesPublishedFilters === true &&
      this.hasInstallmentBoundsFromOffers
    );
  }

  get minEqualsMaxRate(): boolean {
    return (
      this.hasInstallmentBoundsFromOffers &&
      !this.showSingleOfferInstallmentCopy &&
      this.minRate === this.maxRate
    );
  }

  private readNewComparisonValue(filter: unknown): number | null {
    if (filter == null || typeof filter !== 'object') {
      return null;
    }
    const value = (filter as { newComparison?: { value?: unknown } })
      .newComparison?.value;
    return typeof value === 'number' ? value : null;
  }

  getFilterOptions(): void {
    this.filterOptionsSubscription =
      this.resultpageService.filerOptionsDataObserv$.subscribe((data) => {
        if (data !== 1) {
          this.filterDataOptions = data;
        }
      });
  }

  private getOffersForInstallmentBounds(): void {
    this.offersSubscription =
      this.resultpageService.offersDataObserv$.subscribe((data) => {
        if (data === 1) {
          return;
        }
        const offers = Array.isArray(data)
          ? data
          : this.sharedResultService.dataOffers;
        this.refreshInstallmentBoundsFromOffers(offers);
      });
  }

  private readTermsLoanAmountsFromOffers(
    offers: OffersOfApplication[],
  ): number[] {
    if (!Array.isArray(offers) || offers.length === 0) {
      return [];
    }
    return offers
      .map((offer) => offer?.offerResponse?.terms?.loanAmount)
      .filter(
        (v): v is number =>
          typeof v === 'number' && Number.isFinite(v) && !Number.isNaN(v),
      );
  }

  /** Suffix when `newComparison` amount differs from at least one offer `terms.loanAmount`. */
  private updateShowAlternativeSuffix(offers: OffersOfApplication[]): void {
    const comparisonAmount = this.readNewComparisonValue(
      this.sharedResultService.dataFilter,
    );
    const offerLoanAmounts = this.readTermsLoanAmountsFromOffers(offers);
    this.showAlternativeSuffix =
      comparisonAmount !== null &&
      offerLoanAmounts.some((amt) => amt !== comparisonAmount);
  }

  private refreshInstallmentBoundsFromOffers(
    offers?: OffersOfApplication[],
  ): void {
    const list =
      offers ??
      (Array.isArray(this.sharedResultService.dataOffers)
        ? this.sharedResultService.dataOffers
        : []);

    const validNumber = (v: unknown): v is number =>
      typeof v === 'number' && Number.isFinite(v) && !Number.isNaN(v);

    const installments = list
      .map((o) => o?.offerResponse?.terms?.monthlyInstallment)
      .filter(validNumber);

    const goodInstallments = list
      .map((o) => o?.offerResponse?.terms?.goodMonthlyInstallment)
      .filter(validNumber);

    const badInstallments = list
      .map((o) => o?.offerResponse?.terms?.badMonthlyInstallment)
      .filter(validNumber);

    if (installments.length > 0) {
      this.minRate = Math.min(...installments, ...goodInstallments);
      this.maxRate = Math.max(...installments, ...badInstallments);
      this.showSingleOfferInstallmentCopy = list.length === 1;
      this.hasInstallmentBoundsFromOffers = true;
    } else {
      this.minRate = 0;
      this.maxRate = 0;
      this.showSingleOfferInstallmentCopy = false;
      this.hasInstallmentBoundsFromOffers = false;
    }
    this.updateShowAlternativeSuffix(list);
  }

  getLoanData(): void {
    this.loanSubscription =
      this.resultpageService.loanStateDataObserv$.subscribe((data) => {
        if (data !== 1) {
          this.loanFilterData = data;
          this.loanFilterDataValue = data?.value;
          this.loanFilterDataDuration = data?.duration;
        }
      });
  }

  //---check status of loading data, when the loading is finished than show corresponding text---
  checkLoader(): void {
    let arrLoanAmount: any =
      this.filterDataOptions && this.filterDataOptions.loanAmount
        ? this.filterDataOptions.loanAmount
        : {};
    let arrCreditDuration: any =
      this.filterDataOptions && this.filterDataOptions.creditDuration
        ? this.filterDataOptions.creditDuration
        : {};

    this.searchAnimation = false;
    this.finishedSearchAnimation = this.loadedData = this.anyOffers = false;
    this.loanMatchesPublishedFilters = null;

    this.setTimeoutAnyOffers = setTimeout(() => {
      this.search = false;
      if (this.offersData?.length == 0) {
        this.anyOffers = true;
      }
    }, 1000);

    if (this.offersData?.length !== 0) {
      this.setTimeoutLoadedData = setTimeout(() => {
        this.loadedData = true;
        this.finishedSearchAnimation = true;
      }, 1000);

      this.setTimeoutFinishedSearchAnimation = setTimeout(() => {
        this.finishedSearchAnimation = false;
      }, 2500);

      this.setTimeoutFind = setTimeout(() => {
        this.loadedData = false;

        const matches = !!(
          arrLoanAmount &&
          this.loanFilterData &&
          arrLoanAmount.some((el) => el.value == this.loanFilterData.value) &&
          arrCreditDuration &&
          arrCreditDuration.some(
            (el) => el.value == this.loanFilterData.duration,
          )
        );
        this.loanMatchesPublishedFilters = matches;
      }, 3000);
    }
  }
}
